/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import camelCase from 'lodash/camelCase';
import isArray from 'lodash/isArray';

import ContentCardContext from 'components/cards/content-card/context/ContentCardContext';
import PerformanceCard from 'components/cards/performance-card/PerformanceCard';
import PopoverList from 'components/popover-list/PopoverList';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import Spinner from 'components/spinner/Spinner';

import { __t, applyTranslations } from 'utils/language';
import { abbrNum, getMetricValueInPercentage } from 'utils/helper/common';
import { DELIVERABLE_TYPE_INSTAGRAM_STORY } from 'utils/deliverables';

import type { InfluencerCommon, MixedSelectDropdownItem, ReportInfluencerProfile, ReportPost } from 'types';
import type { ContentCardCreatorProfile } from 'components/cards/content-card/types';
import type { ReportingDeliverableTypes } from 'modules/reporting/types/ReportingMetaInfo.type';
import type {
  ReportingMetaInfoIGStorySnippetType,
  ReportingPostsPerformance,
  ReportingSummaryInfluencersMap
} from 'modules/reporting/types';

import './creator-perfomance-widget.scss';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./creator-perfomance-widget.i18n.json'));

type CreatorProfile = ContentCardCreatorProfile | InfluencerCommon;

type Props = {
  isLoading: boolean,
  postsPerformance: ReportingPostsPerformance,
  igStorySnippetType: ReportingMetaInfoIGStorySnippetType,
  dataTypesToShow: Array<ReportingDeliverableTypes>,
  influencersMapById: ReportingSummaryInfluencersMap,
  onNoPostClick: () => void
};

type State = {
  isCollapsed: boolean,
  contentFormatFilter: string,
  kpiFilter: string
};

const KPI_TYPES = ['impressions', 'likes', 'engagement_rate'];
const COMMON_MSG_KEY = 'collabary_common';

const getKPIMapping = (type: string, filter: string): string => {
  if (filter === 'likes') {
    return 'likes';
  }

  if (filter === 'engagement_rate') {
    if (type === 'tiktok_video') {
      return 'engagementRateFollowers';
    }

    return 'engagementRate';
  }

  switch (type) {
    case 'tiktok_video':
      return 'views';
    case 'instagram_story':
      return 'storiesImpressions';
    case 'instagram_post':
      return 'impressions';
    case 'instagram_reel':
      return 'impressions';
    default:
      return '';
  }
};

class CreatorPerformanceWidget extends React.Component<Props, State> {
  state = {
    isCollapsed: true,
    contentFormatFilter: isArray(this.props.dataTypesToShow) ? this.props.dataTypesToShow[0] : 'instagram_post',
    kpiFilter: 'impressions'
  };

  componentDidUpdate(prevProps: Props) {
    const { dataTypesToShow } = this.props;

    if (dataTypesToShow.length !== prevProps.dataTypesToShow.length) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        contentFormatFilter: dataTypesToShow[0]
      });
    }
  }

  handleKPIFilterChange = (items: Array<MixedSelectDropdownItem>): void => {
    const selectedItem = items.find(item => item.isSelected);

    if (selectedItem) {
      this.setState({
        kpiFilter: selectedItem.identifier
      });
    }
  };

  handleContentFormatFilterChange = (items: Array<MixedSelectDropdownItem>): void => {
    const selectedItem = items.find(item => item.isSelected);

    if (selectedItem) {
      this.setState({
        contentFormatFilter: selectedItem.identifier
      });
    }
  };

  getPerfomanceValue = (post: ReportPost) => {
    const { contentFormatFilter, kpiFilter } = this.state;
    const getter = getKPIMapping(contentFormatFilter, kpiFilter);

    const metric = post.metrics[getter] || 0;

    switch (kpiFilter) {
      case 'engagement_rate':
        return getMetricValueInPercentage(metric);
      case 'likes':
        return abbrNum(metric, { decimal: 1 }, true);
      case 'impressions':
        return abbrNum(metric, { decimal: 1 }, true);
      default:
        return '';
    }
  };

  toggleCollapsed = (event: SyntheticEvent<HTMLElement>) => {
    this.setState(state => ({
      isCollapsed: !state.isCollapsed
    }));

    event.preventDefault();
  };

  renderContentFormatFilter = (availableTypes: Array<ReportingDeliverableTypes>): JSX.Element | null => {
    const { dataTypesToShow } = this.props;

    if (availableTypes.indexOf(this.state.contentFormatFilter) === -1 && availableTypes.length) {
      this.setState({
        contentFormatFilter: availableTypes[0]
      });
      return null;
    }

    if (dataTypesToShow.length < 2) {
      return null;
    }

    return (
      <div className="creator-perfomance-widget__filter">
        <div className="creator-perfomance-widget__filter-label">
          {__t('creator_perfomance_widget.filters.labels.content_format')}
        </div>
        <div className="creator-perfomance-widget__filter-select">
          <MixedSelectDropdown
            name="content"
            items={
              dataTypesToShow
                .map(value => ({
                  identifier: value,
                  label: __t(`creator_perfomance_widget.filters.options.content_format.${value}`),
                  value,
                  isSelected: value === this.state.contentFormatFilter,
                  isDisabled: (
                    (this.state.kpiFilter === 'likes') && (value === DELIVERABLE_TYPE_INSTAGRAM_STORY) ||
                    availableTypes.indexOf(value) === -1
                  )
                }))
            }
            onItemsToggle={this.handleContentFormatFilterChange}
          />
        </div>
      </div>
    );
  };

  renderKPIFilter = () => {
    const { dataTypesToShow } = this.props;
    let updatedKpiTypes = KPI_TYPES;

    // remove "likes" if only ig story tasks exist
    if (dataTypesToShow.length === 1 && dataTypesToShow[0] === DELIVERABLE_TYPE_INSTAGRAM_STORY) {
      updatedKpiTypes = updatedKpiTypes.filter(option => option !== 'likes');
    }

    return (
      <div className="creator-perfomance-widget__filter">
        <div className="creator-perfomance-widget__filter-label">
          <div>
            {__t('creator_perfomance_widget.filters.labels.kpi')}
          </div>
          {this.renderPopover()}
        </div>
        <div className="creator-perfomance-widget__filter-select">
          <MixedSelectDropdown
            name="kpi"
            items={updatedKpiTypes.map(value => ({
              identifier: value,
              label: __t(`creator_perfomance_widget.filters.options.kpi.${value}`),
              value,
              isSelected: value === this.state.kpiFilter,
              isDisabled: (this.state.contentFormatFilter === DELIVERABLE_TYPE_INSTAGRAM_STORY) && (value === 'likes')
            }))}
            onItemsToggle={this.handleKPIFilterChange}
          />
        </div>
      </div>
    );
  };

  renderPopover = () => (
    <div className="creator-perfomance-widget__popover">
      <PopoverList
        list={KPI_TYPES.map(value => ({
          title: __t(`${COMMON_MSG_KEY}.kpis.${value}`),
          body: __t(`creator_perfomance_widget.filters.tooltips.${value}`)
        }))}
      />
    </div>
  );

  renderTitle = () => (
    <div className="creator-perfomance-widget__header">
      <span className="creator-perfomance-widget__title">
        {__t('creator_perfomance_widget.title')}
      </span>
    </div>
  );

  render() {
    const {
      dataTypesToShow,
      influencersMapById,
      isLoading,
      postsPerformance,
      igStorySnippetType,
      onNoPostClick
    } = this.props;

    const { contentFormatFilter, kpiFilter, isCollapsed } = this.state;

    if (isLoading) {
      return (
        <div className="creator-perfomance-widget">
          {this.renderTitle()}
          <div className="creator-perfomance-widget__spinner">
            <Spinner
              position="inline"
              size="small"
              color="light"
            />
          </div>
        </div>
      );
    }

    const postsByKPI = postsPerformance[camelCase(kpiFilter)];
    const posts = postsByKPI[camelCase(contentFormatFilter)];

    const availableTypes = dataTypesToShow.filter(value => postsByKPI[camelCase(value)]?.length);

    const filters = (
      <div className="creator-perfomance-widget__filters">
        {this.renderKPIFilter()}
        {
          dataTypesToShow.length > 1 &&
            this.renderContentFormatFilter(availableTypes)
        }
      </div>
    );

    if (!availableTypes.length) {
      return (
        <div className="creator-perfomance-widget">
          {this.renderTitle()}
          {filters}
          <div className="creator-perfomance-widget__empty">
            {__t('creator_perfomance_widget.no_content')}
          </div>
        </div>
      );
    }

    return (
      <div className="creator-perfomance-widget">
        {this.renderTitle()}
        {filters}
        <div className="creator-perfomance-widget__cards">
          {
            posts?.map((post, index) => {
              const creatorProfile = influencersMapById[post.influencerProfileId];

              return creatorProfile && (
                <div
                  key={post.id}
                  className="creator-perfomance-widget__card"
                >
                  <ContentCardContext.Provider value={{ posts: [post] }}>
                    <PerformanceCard
                      value={this.getPerfomanceValue(post)}
                      valueType={index === 0 ? 'highest' : 'lowest'}
                      metricType={kpiFilter}
                      onNoPostClick={onNoPostClick}
                      contentCard={{
                        item: post,
                        index: 0,
                        channel: contentFormatFilter === 'tiktok_video' ? 'tiktok' : 'instagram',
                        creatorProfile,
                        ...(post.type.includes('story') ? { igStorySnippetType } : undefined)
                      }}
                    />
                  </ContentCardContext.Provider>
                </div>
              );
            })
          }
        </div>
        {/* TODO: uncomment the following code when ready */}
        {/* IMPORTANT: remove the eslint-disable at the top of this file when uncommenting */}
        {/* <div className="creator-perfomance-widget__header">
          <div className="creator-perfomance-widget__icon">
            <SVGDynamicIcon
              name={`chevron-${isCollapsed ? 'down' : 'up'}-custom-fg`}
              width={15}
              height={8}
              customColor="grey"
              clickable
              onClick={this.toggleCollapsed}
            />
          </div>
          <span
            onClick={this.toggleCollapsed}
            className="creator-perfomance-widget__title creator-perfomance-widget__title--clickable"
          >
            {__t('creator_perfomance_widget.table.title')}
          </span>
        </div>
        {
          !isCollapsed && (
            <div className="creator-perfomance-widget__table">
              <CampaignReportingDetailsByInfluencer
                influencerProfiles={activeChannelInfluencerProfiles}
                channel={contentFormatFilter === 'tiktok_video' ? 'tiktok' : 'instagram'}
              />
            </div>
          )
        } */}
      </div>
    );
  }
}

export default CreatorPerformanceWidget;
